import React, { useEffect, useState, useContext } from 'react';
import apis from '../../helpers/apis';
import { getLearnActionConfig } from '../../helpers/common';
import { Button, FontIcon, LikeDislike, Text } from '@intigral/shared';
import { __ } from '../../translations';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import { EVENT, KEYS } from '../../contexts/MixpanelConstants';
import { getMixpanelVodProperties, getMixpanelLinearProperties } from '../../contexts/MixpanelHelper';
import { capitalizeFirstLetter } from '@intigral/utils/lib/helpers/string';
import { container } from '@intigral/domain/lib/DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import user from '../../helpers/user';
import { ILearnActionService } from '@intigral/domain/lib/application/services/LearnActionService';
import h, { getCountry } from '../../helpers/common';
import { log } from '@intigral/utils';

type LikeDislikeButtonProps = {
    itemGuid: string;
    item: any;
    handleLikeDislikePopupClose?: (open?: boolean) => void;
    style?: {
        float: string;
        marginTop: string;
    };
    className?: string;
    likeLabel?: string;
    dislikeLabel?: string;
    /** `list` renders separate full-width outline rows for drawer menus */
    variant?: 'inline' | 'list';
    square?: boolean;
    /**
     * Optional callback after the like/dislike API settles (e.g. Continue Watching quick-action
     * analytics). Not used by other carousels.
     */
    onActionResult?: (result: {
        action: 'like' | 'dislike';
        validated: boolean;
        failureReason?: string;
    }) => void;
    /**
     * When true, skips Content Rating - Add/Remove Mixpanel (Continue Watching uses its own
     * quick-action events instead). Detail overlay and other carousels keep the default events.
     */
    skipContentRatingTracking?: boolean;
};

export default function LikeDislikeButton({
    itemGuid,
    item,
    handleLikeDislikePopupClose,
    style,
    className,
    likeLabel = __('like_content'),
    dislikeLabel = __('dislike_content'),
    variant = 'inline',
    onActionResult,
    skipContentRatingTracking = false,
    ...props
}: LikeDislikeButtonProps) {
    const [isContentLiked, setContentLiked] = useState(null);
    const learnActionConfig = getLearnActionConfig();
    const { track }: any = useContext(MixpanelContext);

    useEffect(() => {
        getContentRating();
    }, [itemGuid]);

    /***
     * get the content rating
     * @param {boolean} triggerMixPanelEvent - if true, will trigger mixpanel event
     * @param {string} clickActionType - click action type
     */
    const getContentRating = (triggerMixPanelEvent = false, clickActionType = '') => {
        if (itemGuid) {
            const searchParams = {
                userName: user.getUsername(),
                profileName: user.getProfileName(),
                itemGuid: itemGuid,
            };
            const learnActionService = container.resolve<ILearnActionService>(DI.SERVICES.ILearnActionService);
            learnActionService
                .getContentRating(user.getAPIKey(), searchParams, user.getIdentityToken())
                .then((_res) => {
                    let value = null;
                    if (_res?.isLike === true) {
                        value = 'like';
                    } else if (_res?.isLike === false) {
                        value = 'dislike';
                    }
                    if (triggerMixPanelEvent) {
                        addMixpanelEvent(clickActionType, value === null ? true : false);
                    }
                    setContentLiked(value);
                })
                .catch((_err) => {
                    setContentLiked(null);
                });
        }
    };

    const handleLikeDislike = (actionType = '') => {
        if (itemGuid) {
            const action = actionType === 'like' || actionType === 'dislike' ? actionType : null;
            setContentLiked(actionType === isContentLiked ? null : actionType);
            const searchParams = {
                userName: user.getUsername(),
                profileName: user.getProfileName(),
                itemGuid: itemGuid,
                action: actionType,
                device: h.getDevice(),
                country: getCountry(),
                // useCaseId: "string",
            };
            const learnActionService = container.resolve<ILearnActionService>(DI.SERVICES.ILearnActionService);
            learnActionService
                .addLikeDislike(user.getAPIKey(), searchParams, user.getIdentityToken())
                .then((_res) => {
                    // addMixpanelEvent(actionType, actionType === isContentLiked ? true : false);
                    if (_res === true && handleLikeDislikePopupClose) {
                        handleLikeDislikePopupClose();
                    }
                    if (action) {
                        onActionResult?.({ action, validated: true });
                    }
                    getContentRating(!skipContentRatingTracking, actionType);
                })
                .catch((error) => {
                    setContentLiked(isContentLiked);
                    if (action) {
                        const failureReason =
                            (typeof error === 'string' && error) ||
                            error?.message ||
                            error?.error?.message ||
                            'Unknown error';
                        onActionResult?.({ action, validated: false, failureReason });
                    }
                });
        }
    };

    const addMixpanelEvent = (actionType, isRemove = false) => {
        track(isRemove ? EVENT.CONTENT_RATING.CONTENT_RATING_REMOVE : EVENT.CONTENT_RATING.CONTENT_RATING_ADD, {
            [KEYS.GENERAL.RATING_TYPE]: capitalizeFirstLetter(actionType),
            ...(item.category === 'VOD' ? getMixpanelVodProperties(item) : getMixpanelLinearProperties(item)),
        });
    };

    if (!learnActionConfig?.isLikeDislikeEnable) {
        return <></>;
    }

    if (variant === 'list') {
        return (
            <>
                <Button
                    fullWidth
                    compact
                    className={className}
                    data-test-id="dislike-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleLikeDislike('dislike');
                    }}
                >
                    <FontIcon
                        name={isContentLiked === 'dislike' ? 'thumb-down-filled' : 'thumb-down'}
                        size="small"
                        color={isContentLiked === 'dislike' ? 'orange' : ''}
                    />
                    <span>
                        <Text as="p" variant="text3">
                            {dislikeLabel}
                        </Text>
                    </span>
                </Button>
                <Button
                    fullWidth
                    compact
                    className={className}
                    data-test-id="like-button"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleLikeDislike('like');
                    }}
                >
                    <FontIcon
                        name={isContentLiked === 'like' ? 'thumb-up-filled' : 'thumb-up'}
                        size="small"
                        color={isContentLiked === 'like' ? 'orange' : ''}
                    />
                    <span>
                        <Text as="p" variant="text3">
                            {likeLabel}
                        </Text>
                    </span>
                </Button>
            </>
        );
    }

    return (
        <LikeDislike
            likeLabel={likeLabel}
            dislikeLabel={dislikeLabel}
            isContentLiked={isContentLiked}
            onActionClick={handleLikeDislike}
            className={className}
            {...(style ? { style: style as React.CSSProperties } : {})}
            {...props}
        />
    );
}
